import { useState, useMemo } from "react";
import { apiClient } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Plus, PencilSimple, EyeSlash, Eye, Target, Check, DotsSixVertical } from "@phosphor-icons/react";
import { SectionTitle, SourceTag } from "./PerfUI";

const UNIT_LABEL = { number: "nombre", currency: "€", percent: "%", duration: "durée" };
const BASIS_SHORT = { day: "jour", week: "semaine", month: "mois" };
const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const fmt = (v, unit) => {
  if (v == null) return "—";
  if (unit === "currency") return `${v.toLocaleString("fr-FR")} €`;
  if (unit === "percent") return `${v.toLocaleString("fr-FR")} %`;
  if (unit === "duration") return `${v} min`;
  return v.toLocaleString("fr-FR");
};

export default function PerfCatalogue({ services, metrics, collaborators, targets, reload }) {
  const [serviceDlg, setServiceDlg] = useState(null);
  const [metricDlg, setMetricDlg] = useState(null);
  const [targetDlg, setTargetDlg] = useState(null);
  const [collabDlg, setCollabDlg] = useState(null);
  const [orderIds, setOrderIds] = useState(null);
  const [dragId, setDragId] = useState(null);
  const [overId, setOverId] = useState(null);

  const metricRows = useMemo(() => {
    if (!orderIds) return metrics;
    const map = Object.fromEntries(metrics.map((m) => [m.id, m]));
    const ordered = orderIds.map((id) => map[id]).filter(Boolean);
    return [...ordered, ...metrics.filter((m) => !orderIds.includes(m.id))];
  }, [metrics, orderIds]);

  const dropOn = async (targetId) => {
    setOverId(null);
    if (!dragId || dragId === targetId) return setDragId(null);
    const ids = metricRows.map((m) => m.id);
    const from = ids.indexOf(dragId);
    const to = ids.indexOf(targetId);
    if (from < 0 || to < 0) return setDragId(null);
    ids.splice(to, 0, ids.splice(from, 1)[0]);
    setOrderIds(ids);
    setDragId(null);
    try {
      await apiClient.put("/performance/metrics/reorder", { ids });
      toast.success("Ordre des indicateurs enregistré");
      reload();
    } catch (e) {
      setOrderIds(null);
      toast.error(e?.response?.data?.detail || "L'ordre n'a pas pu être enregistré");
    }
  };

  const targetOf = (metricId) => targets.find((t) => t.metric_id === metricId) || null;

  const toggleService = async (s) => {
    try {
      await apiClient.patch(`/performance/services/${s.id}`, { active: !s.active });
      toast.success(s.active ? "Service désactivé" : "Service réactivé");
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  const toggleMetric = async (m) => {
    try {
      await apiClient.patch(`/performance/metrics/${m.id}`, { active: !m.active });
      toast.success(m.active ? "Indicateur désactivé (historique conservé)" : "Indicateur réactivé");
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  const toggleCollab = async (c) => {
    try {
      await apiClient.patch(`/performance/collaborators/${c.id}`, { active: !c.active });
      toast.success(c.active ? "Collaborateur désactivé (historique conservé)" : "Collaborateur réactivé");
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  return (
    <>
      <SectionTitle right={
        <Button variant="outline" onClick={() => setServiceDlg({ name: "" })} className="rounded-sm h-9 border-zinc-300 gap-2 text-xs" data-testid="catalogue-add-service">
          <Plus size={14} weight="bold" /> Nouveau service
        </Button>
      }>
        Services <span className="text-zinc-400 font-normal text-sm">({services.length})</span>
      </SectionTitle>
      <div className="flex flex-wrap gap-2" data-testid="catalogue-services">
        {services.map((s) => (
          <span key={s.id} className={`inline-flex items-center gap-2 border px-3 h-9 text-sm ${s.active ? "border-zinc-300 bg-white" : "border-dashed border-zinc-300 bg-zinc-50 text-zinc-400"}`} data-testid={`catalogue-service-${s.id}`}>
            {s.active ? <Check size={13} weight="bold" className="text-green-600" /> : <EyeSlash size={13} />}
            {s.name}
            <button onClick={() => setServiceDlg(s)} title="Renommer" className="text-zinc-400 hover:text-zinc-900" data-testid={`service-edit-${s.id}`}><PencilSimple size={13} /></button>
            <button onClick={() => toggleService(s)} title={s.active ? "Désactiver" : "Réactiver"} className="text-zinc-400 hover:text-zinc-900" data-testid={`service-toggle-${s.id}`}>
              {s.active ? <EyeSlash size={13} /> : <Eye size={13} />}
            </button>
          </span>
        ))}
      </div>

      <SectionTitle right={
        <Button variant="outline" onClick={() => setMetricDlg({ unit: "number", direction: "up", source: "MANUAL", service_id: services[0]?.id })} className="rounded-sm h-9 border-zinc-300 gap-2 text-xs" data-testid="catalogue-add-metric">
          <Plus size={14} weight="bold" /> Nouvel indicateur
        </Button>
      }>
        Indicateurs <span className="text-zinc-400 font-normal text-sm">({metrics.length})</span>
      </SectionTitle>
      <div className="bg-white border border-zinc-200 overflow-x-auto" data-testid="catalogue-metrics">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-zinc-200 bg-zinc-50">
              <Th /><Th>Indicateur</Th><Th>Service</Th><Th>Unité</Th><Th>Sens</Th><Th>Source</Th><Th right>Objectif</Th><Th right>Actions</Th>
            </tr>
          </thead>
          <tbody>
            {metricRows.map((m) => (
              <tr
                key={m.id}
                draggable
                onDragStart={(e) => { e.dataTransfer.effectAllowed = "move"; setDragId(m.id); }}
                onDragEnd={() => { setDragId(null); setOverId(null); }}
                onDragOver={(e) => { e.preventDefault(); setOverId(m.id); }}
                onDrop={() => dropOn(m.id)}
                className={`border-b border-zinc-100 last:border-0 hover:bg-zinc-50 ${m.active ? "" : "opacity-50"} ${dragId === m.id ? "opacity-40" : ""} ${overId === m.id && dragId !== m.id ? "bg-zinc-100 border-t-2 border-t-zinc-900" : ""}`}
                data-testid={`catalogue-metric-${m.id}`}
              >
                <td className="pl-3 pr-0 py-3 w-8 cursor-grab active:cursor-grabbing" title="Glisser pour réordonner">
                  <DotsSixVertical size={16} weight="bold" className="text-zinc-300 hover:text-zinc-700" data-testid={`metric-drag-${m.id}`} />
                </td>
                <td className="px-4 py-3 font-medium">
                  {m.name}
                  {m.per_collaborator && <span className="ml-2 text-[10px] text-zinc-500 border border-zinc-200 px-1.5">par collaborateur</span>}
                  {m.snapshot && <span className="ml-2 text-[10px] text-zinc-500 border border-zinc-200 px-1.5">état fin de jour</span>}
                  {m.formula && <div className="text-[10px] text-zinc-400 mt-0.5">{prettyFormula(m.formula, metrics)}</div>}
                </td>
                <td className="px-4 py-3 text-zinc-500">{services.find((s) => s.id === m.service_id)?.name || "—"}</td>
                <td className="px-4 py-3 text-zinc-500">{UNIT_LABEL[m.unit]}</td>
                <td className="px-4 py-3 text-zinc-500">{m.direction === "up" ? "Plus haut = mieux" : "Plus bas = mieux"}</td>
                <td className="px-4 py-3"><SourceTag source={m.source} /></td>
                <td className="px-4 py-3 text-right tabular-nums">
                  {targetOf(m.id) ? (
                    <>
                      {fmt(targetOf(m.id).value, m.unit)}
                      <span className="text-zinc-400 text-xs"> /{BASIS_SHORT[targetOf(m.id).basis || "day"]}</span>
                    </>
                  ) : "—"}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-1">
                    <IconBtn icon={Target} label="Objectif" onClick={() => setTargetDlg({ metric: m, value: targetOf(m.id)?.value ?? "", basis: targetOf(m.id)?.basis || "day", effective_from: todayISO() })} testId={`metric-target-${m.id}`} />
                    <IconBtn icon={PencilSimple} label="Modifier" onClick={() => setMetricDlg(m)} testId={`metric-edit-${m.id}`} />
                    <IconBtn icon={m.active ? EyeSlash : Eye} label={m.active ? "Désactiver" : "Réactiver"} onClick={() => toggleMetric(m)} testId={`metric-toggle-${m.id}`} />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-3 border border-zinc-200 bg-zinc-50 px-4 py-3 text-xs text-zinc-600">
        Glissez une ligne par sa poignée pour changer l'ordre d'affichage des indicateurs (saisie, tableau de bord et export suivent cet ordre). Désactiver un indicateur le retire de la saisie et du tableau, mais conserve tout son historique. Les objectifs sont datés : en modifier un ne change pas les atteintes passées.
      </div>

      <SectionTitle right={
        <Button onClick={() => setCollabDlg({ name: "", service_id: services.filter((s) => s.active)[0]?.id })} className="bg-zinc-900 hover:bg-zinc-800 text-white rounded-sm h-9 gap-2 text-xs" data-testid="catalogue-add-collab">
          <Plus size={14} weight="bold" /> Nouveau collaborateur
        </Button>
      }>
        Collaborateurs <span className="text-zinc-400 font-normal text-sm">({collaborators.filter((c) => c.active).length} actif(s))</span>
      </SectionTitle>
      {collaborators.length === 0 ? (
        <div className="bg-white border border-dashed border-zinc-300 px-5 py-8 text-center text-sm text-zinc-500" data-testid="catalogue-collabs-empty">
          Aucun collaborateur. Ajoutez-les pour pouvoir saisir les résultats personne par personne.
        </div>
      ) : (
        <div className="bg-white border border-zinc-200 overflow-x-auto" data-testid="catalogue-collabs">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-zinc-200 bg-zinc-50">
                <Th>Collaborateur</Th><Th>Service</Th><Th>Indicateurs suivis</Th><Th>Statut</Th><Th right>Actions</Th>
              </tr>
            </thead>
            <tbody>
              {collaborators.map((c) => {
                const suivis = metrics.filter((m) => m.active && m.per_collaborator && m.service_id === c.service_id);
                return (
                  <tr key={c.id} className={`border-b border-zinc-100 last:border-0 hover:bg-zinc-50 ${c.active ? "" : "opacity-50"}`} data-testid={`catalogue-collab-${c.id}`}>
                    <td className="px-4 py-3 font-medium">{c.name}</td>
                    <td className="px-4 py-3 text-zinc-500">{services.find((s) => s.id === c.service_id)?.name || "—"}</td>
                    <td className="px-4 py-3 text-zinc-500">
                      {suivis.length === 0 ? <span className="text-amber-700">aucun — cochez « par collaborateur » sur un indicateur</span> : suivis.map((m) => m.name).join(" · ")}
                    </td>
                    <td className="px-4 py-3">
                      {c.active
                        ? <span className="text-[10px] font-semibold uppercase tracking-wide text-green-700 bg-green-50 border border-green-200 px-1.5 py-0.5">Actif</span>
                        : <span className="text-[10px] font-semibold uppercase tracking-wide text-zinc-500 bg-zinc-100 border border-zinc-200 px-1.5 py-0.5">Inactif</span>}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <IconBtn icon={PencilSimple} label="Modifier" onClick={() => setCollabDlg(c)} testId={`collab-edit-${c.id}`} />
                        <IconBtn icon={c.active ? EyeSlash : Eye} label={c.active ? "Désactiver" : "Réactiver"} onClick={() => toggleCollab(c)} testId={`collab-toggle-${c.id}`} />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <ServiceDialog state={serviceDlg} onClose={() => setServiceDlg(null)} reload={reload} />
      <MetricDialog state={metricDlg} services={services} metrics={metrics} onClose={() => setMetricDlg(null)} reload={reload} />
      <TargetDialog state={targetDlg} onClose={() => setTargetDlg(null)} reload={reload} />
      <CollabDialog state={collabDlg} services={services} onClose={() => setCollabDlg(null)} reload={reload} />
    </>
  );
}

function ServiceDialog({ state, onClose, reload }) {
  const [name, setName] = useState("");
  const open = !!state;
  const editing = state?.id;
  const value = name || (open ? state.name || "" : "");

  const submit = async () => {
    const payload = { name: value.trim() };
    if (!payload.name) return toast.error("Nom obligatoire");
    try {
      if (editing) await apiClient.patch(`/performance/services/${state.id}`, payload);
      else await apiClient.post("/performance/services", payload);
      toast.success(editing ? "Service renommé" : "Service créé");
      setName(""); onClose(); reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { setName(""); onClose(); } }}>
      <DialogContent className="rounded-sm" data-testid="service-dialog">
        <DialogHeader><DialogTitle className="font-heading">{editing ? "Renommer le service" : "Nouveau service"}</DialogTitle></DialogHeader>
        <div>
          <Label className="label-tiny text-zinc-500">Nom du service</Label>
          <Input value={value} onChange={(e) => setName(e.target.value)} placeholder="Ex : Préparation moteurs" className="h-10 rounded-sm border-zinc-300 mt-1" data-testid="service-name-input" />
        </div>
        <DialogFooter>
          <Button onClick={submit} className="bg-zinc-900 hover:bg-zinc-800 text-white rounded-sm" data-testid="service-submit">Enregistrer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MetricDialog({ state, services, metrics, onClose, reload }) {
  const [form, setForm] = useState(null);
  const open = !!state;
  const data = form || state || {};
  const editing = state?.id;
  const setF = (k, v) => setForm({ ...data, [k]: v });

  const submit = async () => {
    const payload = {
      name: (data.name || "").trim(),
      service_id: data.service_id,
      unit: data.unit || "number",
      direction: data.direction || "up",
      source: data.source || "MANUAL",
      formula: data.source === "CALCULATED" ? (data.formula || "").trim() : null,
      snapshot: !!data.snapshot,
      per_collaborator: !!data.per_collaborator,
    };
    if (!payload.name) return toast.error("Nom obligatoire");
    if (!payload.service_id) return toast.error("Service obligatoire");
    if (payload.source === "CALCULATED" && !payload.formula) return toast.error("Formule obligatoire pour un indicateur calculé");
    try {
      if (editing) await apiClient.patch(`/performance/metrics/${state.id}`, payload);
      else await apiClient.post("/performance/metrics", payload);
      toast.success(editing ? "Indicateur modifié" : "Indicateur créé");
      setForm(null); onClose(); reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { setForm(null); onClose(); } }}>
      <DialogContent className="rounded-sm max-w-lg max-h-[90vh] overflow-y-auto" data-testid="metric-dialog">
        <DialogHeader><DialogTitle className="font-heading">{editing ? "Modifier l'indicateur" : "Nouvel indicateur"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="label-tiny text-zinc-500">Nom</Label>
            <Input value={data.name || ""} onChange={(e) => setF("name", e.target.value)} placeholder="Ex : Moteurs préparés" className="h-10 rounded-sm border-zinc-300 mt-1" data-testid="metric-name-input" />
          </div>
          <div>
            <Label className="label-tiny text-zinc-500">Service</Label>
            <Select value={data.service_id || ""} onValueChange={(v) => setF("service_id", v)}>
              <SelectTrigger className="h-10 rounded-sm border-zinc-300 mt-1" data-testid="metric-service-select"><SelectValue placeholder="Choisir" /></SelectTrigger>
              <SelectContent>{services.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="label-tiny text-zinc-500">Unité</Label>
              <Select value={data.unit || "number"} onValueChange={(v) => setF("unit", v)}>
                <SelectTrigger className="h-10 rounded-sm border-zinc-300 mt-1" data-testid="metric-unit-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="number">Nombre</SelectItem>
                  <SelectItem value="currency">Euros (€)</SelectItem>
                  <SelectItem value="percent">Pourcentage (%)</SelectItem>
                  <SelectItem value="duration">Durée (min)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-tiny text-zinc-500">Sens</Label>
              <Select value={data.direction || "up"} onValueChange={(v) => setF("direction", v)}>
                <SelectTrigger className="h-10 rounded-sm border-zinc-300 mt-1" data-testid="metric-direction-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="up">Plus élevé = meilleur</SelectItem>
                  <SelectItem value="down">Plus bas = meilleur</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="label-tiny text-zinc-500">Source</Label>
            <Select value={data.source || "MANUAL"} onValueChange={(v) => setF("source", v)}>
              <SelectTrigger className="h-10 rounded-sm border-zinc-300 mt-1" data-testid="metric-source-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="MANUAL">Saisie quotidienne</SelectItem>
                <SelectItem value="CALCULATED">Calculé (formule)</SelectItem>
                <SelectItem value="API">API (connecteur à venir)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {data.source === "CALCULATED" && (
            <FormulaBuilder
              value={data.formula || ""}
              metrics={metrics.filter((m) => m.id !== state?.id && m.source !== "CALCULATED")}
              onChange={(v) => setF("formula", v)}
            />
          )}
          <div className="flex flex-col gap-2 pt-1">
            <Check2 label="Suivi par collaborateur" checked={!!data.per_collaborator} onChange={(v) => setF("per_collaborator", v)} testId="metric-percollab" />
            <Check2 label="État de fin de journée (ne s'additionne pas sur la semaine / le mois)" checked={!!data.snapshot} onChange={(v) => setF("snapshot", v)} testId="metric-snapshot" />
          </div>
        </div>
        <DialogFooter>
          <Button onClick={submit} className="bg-zinc-900 hover:bg-zinc-800 text-white rounded-sm" data-testid="metric-submit">Enregistrer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TargetDialog({ state, onClose, reload }) {
  const [form, setForm] = useState(null);
  const open = !!state;
  const data = form || state || {};

  const submit = async () => {
    const raw = String(data.value ?? "").replace(",", ".").trim();
    if (raw === "" || Number.isNaN(Number(raw))) return toast.error("Valeur numérique obligatoire");
    try {
      await apiClient.post("/performance/targets", {
        metric_id: state.metric.id,
        value: Number(raw),
        basis: data.basis || "day",
        effective_from: data.effective_from,
      });
      toast.success("Objectif enregistré");
      setForm(null); onClose(); reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { setForm(null); onClose(); } }}>
      <DialogContent className="rounded-sm" data-testid="target-dialog">
        <DialogHeader><DialogTitle className="font-heading">Objectif — {state?.metric?.name}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="label-tiny text-zinc-500">Objectif ({UNIT_LABEL[state?.metric?.unit]})</Label>
            <Input value={data.value ?? ""} onChange={(e) => setForm({ ...data, value: e.target.value })} inputMode="decimal" className="h-10 rounded-sm border-zinc-300 mt-1 tabular-nums" data-testid="target-value-input" />
          </div>
          <div>
            <Label className="label-tiny text-zinc-500">Cet objectif est défini</Label>
            <Select value={data.basis || "day"} onValueChange={(v) => setForm({ ...data, basis: v })}>
              <SelectTrigger className="h-10 rounded-sm border-zinc-300 mt-1" data-testid="target-basis-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="day">Par jour</SelectItem>
                <SelectItem value="week">Par semaine (5 jours ouvrés)</SelectItem>
                <SelectItem value="month">Par mois (21 jours ouvrés)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="label-tiny text-zinc-500">En vigueur à partir du</Label>
            <Input type="date" value={data.effective_from || todayISO()} onChange={(e) => setForm({ ...data, effective_from: e.target.value })} className="h-10 rounded-sm border-zinc-300 mt-1 font-mono" data-testid="target-date-input" />
          </div>
          <p className="text-[11px] text-zinc-500">L'ancien objectif reste conservé pour les périodes précédentes. Sur le tableau de bord, l'objectif est ramené au nombre de jours réellement saisis dans la période (hors % et états de fin de journée).</p>
        </div>
        <DialogFooter>
          <Button onClick={submit} className="bg-zinc-900 hover:bg-zinc-800 text-white rounded-sm" data-testid="target-submit">Enregistrer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CollabDialog({ state, services, onClose, reload }) {
  const [form, setForm] = useState(null);
  const open = !!state;
  const data = form || state || {};
  const editing = state?.id;

  const submit = async () => {
    const payload = { name: (data.name || "").trim(), service_id: data.service_id };
    if (!payload.name) return toast.error("Nom obligatoire");
    if (!payload.service_id) return toast.error("Service obligatoire");
    try {
      if (editing) await apiClient.patch(`/performance/collaborators/${state.id}`, payload);
      else await apiClient.post("/performance/collaborators", payload);
      toast.success(editing ? "Collaborateur modifié" : "Collaborateur ajouté");
      setForm(null); onClose(); reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { setForm(null); onClose(); } }}>
      <DialogContent className="rounded-sm" data-testid="collab-dialog">
        <DialogHeader><DialogTitle className="font-heading">{editing ? "Modifier le collaborateur" : "Nouveau collaborateur"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="label-tiny text-zinc-500">Nom</Label>
            <Input value={data.name || ""} onChange={(e) => setForm({ ...data, name: e.target.value })} className="h-10 rounded-sm border-zinc-300 mt-1" data-testid="collab-name-input" />
          </div>
          <div>
            <Label className="label-tiny text-zinc-500">Service</Label>
            <Select value={data.service_id || ""} onValueChange={(v) => setForm({ ...data, service_id: v })}>
              <SelectTrigger className="h-10 rounded-sm border-zinc-300 mt-1" data-testid="collab-service-select"><SelectValue placeholder="Choisir" /></SelectTrigger>
              <SelectContent>{services.filter((s) => s.active).map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button onClick={submit} className="bg-zinc-900 hover:bg-zinc-800 text-white rounded-sm" data-testid="collab-submit">Enregistrer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const Check2 = ({ label, checked, onChange, testId }) => (
  <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
    <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="w-4 h-4 accent-zinc-900" data-testid={testId} />
    <span className="text-zinc-700">{label}</span>
  </label>
);

export function prettyFormula(formula, metrics) {
  if (!formula) return "";
  return formula
    .replace(/\[\[([0-9a-fA-F-]{36})\]\]/g, (_, id) => {
      const m = metrics.find((x) => x.id === id);
      return m ? `« ${m.name} »` : "« indicateur supprimé »";
    })
    .replace(/\*/g, "×")
    .replace(/\//g, "÷");
}

function FormulaBuilder({ value, metrics, onChange }) {
  const [pick, setPick] = useState("");
  const append = (txt) => onChange(`${value}${value && !value.endsWith("(") ? " " : ""}${txt}`);
  return (
    <div data-testid="formula-builder">
      <Label className="label-tiny text-zinc-500">Formule</Label>
      <div className="mt-1 min-h-10 border border-zinc-300 bg-zinc-50 px-3 py-2 text-sm" data-testid="formula-preview">
        {value ? prettyFormula(value, metrics) : <span className="text-zinc-400">Composez la formule ci-dessous</span>}
      </div>
      <div className="flex flex-wrap items-center gap-2 mt-2">
        <Select value={pick} onValueChange={(v) => { append(`[[${v}]]`); setPick(""); }}>
          <SelectTrigger className="h-9 rounded-sm border-zinc-300 w-[220px] text-xs" data-testid="formula-metric-select">
            <SelectValue placeholder="+ Insérer un indicateur" />
          </SelectTrigger>
          <SelectContent>
            {metrics.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
          </SelectContent>
        </Select>
        {[["+", "+"], ["−", "-"], ["×", "*"], ["÷", "/"], ["(", "("], [")", ")"], ["100", "100"]].map(([label, token]) => (
          <button
            key={token}
            type="button"
            onClick={() => append(token)}
            className="w-9 h-9 border border-zinc-300 bg-white text-sm hover:bg-zinc-50"
            data-testid={`formula-op-${token}`}
          >
            {label}
          </button>
        ))}
        <button type="button" onClick={() => onChange("")} className="h-9 px-3 border border-zinc-300 bg-white text-xs hover:bg-zinc-50" data-testid="formula-clear">
          Effacer
        </button>
      </div>
      <p className="text-[11px] text-zinc-500 mt-2">
        Exemple : « Retours » ÷ « Pièces expédiées » × 100. Le calcul est automatique sur la période affichée ; si une valeur manque, le résultat affiche « — ».
      </p>
    </div>
  );
}

const IconBtn = ({ icon: Icon, label, onClick, testId }) => (
  <button onClick={onClick} title={label} data-testid={testId} className="w-8 h-8 border border-zinc-200 flex items-center justify-center text-zinc-500 hover:text-zinc-900 hover:border-zinc-400 transition-colors">
    <Icon size={15} weight="bold" />
  </button>
);

const Th = ({ children, right }) => (
  <th className={`label-tiny text-zinc-400 font-medium px-4 py-3 ${right ? "text-right" : "text-left"}`}>{children || ""}</th>
);
